import React, { useEffect, useState } from "react";
import axiosInstance from "../../../axiosInstance";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import {
  Box,
  Typography,
  Skeleton,
  Stack,
  Chip,
  Card,
  CardMedia,
  CardContent,
  useTheme,
  Button,
  Link,
  Collapse,
} from "@mui/material";
import { CalendarMonth } from "@mui/icons-material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import PlaceIcon from "@mui/icons-material/Place";

import { useSelector } from "react-redux";
import BookModal from "../../../pages/SpecialOffers/BookModal";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

export default function BookingTheGap({ type }) {
  const [accommodations, setAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [bookModalOpen, setBookModalOpen] = useState(false);

  const [hoveredIndex, setHoveredIndex] = useState(null);

  const lightMode = useSelector((state) => state.mode.lightMode);

  const theme = useTheme();

  const { t, i18n } = useTranslation();

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.append("api_key", "O0AUQ0KLAD3I71K8YKR82Y6AP");

      const response = await axiosInstance.post(
        "https://webservice.londonist.co.uk/v1/DMCGetAvailableResidencesGAP",
        params,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const processedData = processAccommodationData(response.data.result);
      setAccommodations(processedData);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch data. Please try again later.");
      setLoading(false);
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchData();

    // For demo purposes, we'll use the provided data directly
    const processedData = processAccommodationData(demoData);
    setAccommodations(processedData);
    setLoading(false);

    // Uncomment this to fetch from actual API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processAccommodationData = (data) => {
    const flattenedData = [];

    Object.keys(data).forEach((provider) => {
      Object.keys(data[provider]).forEach((propertyName) => {
        const property = data[provider][propertyName];

        Object.keys(property.room_types).forEach((roomTypeName) => {
          const roomType = property.room_types[roomTypeName];

          roomType.available_rooms.forEach((roomGroup) => {
            roomGroup.forEach((room) => {
              flattenedData.push({
                provider,
                propertyName: property.property_name,
                propertyAddress: property.property_address,
                nearByAdresses: property?.property_near_by_universities,
                propertyImage: property.property_image,
                propertyFacilities: property.property_facilities || [],
                propertyDescription: property.property_description || "",
                roomTypeName,
                roomKind: roomType.room_kind,
                roomImages: roomType.room_images,
                description: roomType.description,
                bedCount: roomType.bed_count,
                dailyPrice: Number.parseFloat(room.daily_price),
                normalDailyPrice: Number.parseFloat(room.normal_daily_price),
                totalPrice: Number.parseFloat(room.total_price),
                duration: Number.parseInt(room.duration),
                checkin: room.checkin,
                checkout: room.checkout,
                roomId: room.room_id,
                id: room.id,
              });
            });
          });
        });
      });
    });

    // En ucuzdan pahalıya sırala
    const sortedRooms = [...flattenedData].sort(
      (a, b) => a.dailyPrice - b.dailyPrice
    );

    // Benzersiz propertyName'e göre filtrele
    const uniqueRoomsByProperty = [];
    const seenProperties = new Set();

    for (const room of sortedRooms) {
      if (!seenProperties.has(room.propertyName)) {
        seenProperties.add(room.propertyName);
        uniqueRoomsByProperty.push(room);
      }
      if (uniqueRoomsByProperty.length >= 9) break; // İlk 9 farklı property
    }

    return uniqueRoomsByProperty;
  };

  if (loading) {
    return <Skeleton variant="rectangular" width="100%" height={400} />;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  const itemsToShow = type === "non-carousel" ? accommodations.slice(0, 3) : accommodations;
  const isCarousel = type !== "non-carousel";

  const cardContent = (item, index) => (
    <Card
      sx={{
        overflow: "hidden",
        boxShadow: 0,
        width: "100%",
        minWidth: 0,
        position: "relative",
        borderRadius: "1rem",
        backgroundColor: lightMode ? "#F4F6F8" : "#212121",
        "&:hover": {
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}
                  >
                    <Box width={"100%"}>
                      <Box
                        pb={1}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderBottom: "1px dashed #dfdfdf",
                        }}
                      >
                        <Typography
                          display={"flex"}
                          variant="body1"
                          alignItems={"center"}
                          gap={1}
                          fontWeight={600}
                          color="text.dark"
                        >
                          <PlaceIcon
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          />
                          {item.propertyName}
                        </Typography>
                        <Stack
                          direction="row"
                          alignItems={"center"}
                          spacing={1}
                        >
                          <Chip
                            size="small"
                            sx={{
                              px: 1,
                              py: 1,
                            }}
                            variant="outlined"
                            color="primary"
                            icon={<CalendarMonth />}
                            label={`${item.duration} ${item?.duration > 1 ? t("night") : t("night")}`}
                          />
                        </Stack>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.dark"
                        display={"flex"}
                        alignItems={"center"}
                        // justifyContent={"center"}
                        gap={1}
                        fontWeight={600}
                        mt={2}
                        border={"1px dashed #dfdfdf"}
                        padding={2}
                        bgcolor={lightMode ? "#fff" : "#292929"}
                        borderRadius={"1rem"}
                        textAlign={"center"}
                      >
                        {/* <MeetingRoomIcon
                      sx={{
                        color: theme.palette.primary.main,
                      }}
                    /> */}
                        {item.roomTypeName}
                      </Typography>

                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        gap={1}
                        mt={2}
                      >
                        <Typography
                          variant="caption"
                          color="text.dark"
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                          fontWeight={600}
                        >
                          <EventAvailableIcon
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          />
                          {/*      {new Date(item.checkin).toLocaleString(i18n.language, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })} */}
                          {dayjs(item.checkin)
                            .locale(i18n.language === "cn" ? "zh-cn" : i18n.language)
                            .format("DD MMM YYYY")}

                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.dark"
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                          fontWeight={600}
                        >
                          /
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.dark"
                          display={"flex"}
                          alignItems={"center"}
                          gap={1}
                          fontWeight={600}
                        >
                          <EventBusyIcon
                            sx={{
                              color: theme.palette.primary.main,
                            }}
                          />

                          {/*     {new Date(item.checkout).toLocaleString(i18n.language, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })} */}

                          {dayjs(item.checkout)
                            .locale(i18n.language === "cn" ? "zh-cn" : i18n.language)
                            .format("DD MMM YYYY")}

                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box padding={2}>
                    <CardMedia
                      component="img"
                      image={item.roomImages?.[0]}
                      alt="accommodation"
                      sx={{
                        width: "100%",
                        height: 280,
                        minHeight: 280,
                        objectFit: "cover",
                        borderRadius: "1rem",
                      }}
                    />

                    <CardContent
                      sx={{
                        position: "absolute",
                        bottom: 16,
                        left: 16,
                      }}
                    >
                      <Collapse
                        orientation="vertical"
                        in={hoveredIndex?.index === index}
                      >
                        <Link
                          sx={{
                            cursor: "pointer",
                            textDecoration: "none",
                          }}
                          onClick={() => {
                            if (hoveredIndex?.index === index) {
                              setSelectedAccommodation(item);
                              setBookModalOpen(true);
                            }
                          }}
                        >
                          <Typography
                            variant="body1"
                            margin={0}
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              fontWeight: 700,
                              background: "#1c252e",
                              color: "#fff",
                              padding: ".25rem .5rem",
                              borderRadius: "1rem",
                            }}
                          >
                            {t("enquiry")} / {t("bookNow")}
                          </Typography>
                        </Link>
                      </Collapse>
                    </CardContent>
                    <CardContent
                      sx={{
                        padding: "0 !important",
                        position: "absolute",
                        bottom: 32,
                        right: 32,
                      }}
                    >
                      <Typography
                        variant="body1"
                        margin={0}
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          fontWeight: 700,
                          background: "#1c252e",
                          color: "#fff",
                          padding: ".25rem .5rem",
                          borderRadius: "1rem",
                        }}
                      >
                        🔥 £{item.dailyPrice.toFixed(2)}
                      </Typography>
                    </CardContent>
                  </Box>
                </Card>
  );

  return (
    <>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
        {type !== "non-carousel" ? (
          <Typography
            variant="h5"
            color={lightMode ? theme.palette.customRed.orange : "#ffffff"}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: "700",
              mr: "auto",
              width: "100%",
              p: 2,
              borderBottom: `1px dashed ${lightMode ? `#e9ecee` : `#333e47`} `,
              textTransform: "uppercase",
            }}
          >
            {t("bookTheGap")}
          </Typography>
        ) : null}

        <Swiper
          style={{
            padding: 16,
            marginTop: 0,
            paddingBottom: 32,
            width: "100%",
            overflow: "hidden",
          }}
          spaceBetween={20}
          slidesPerView={isCarousel ? 1 : 3}
          navigation={isCarousel}
          allowTouchMove={isCarousel}
          modules={isCarousel ? [Navigation] : []}
          breakpoints={
            isCarousel
              ? {
                  767: { slidesPerView: 2 },
                  1439: { slidesPerView: 3 },
                  1921: { slidesPerView: 4 },
                }
              : {
                  767: { slidesPerView: 2 },
                  1439: { slidesPerView: 3 },
                }
          }
        >
          {itemsToShow.map((item, index) => (
            <SwiperSlide key={index} style={{ height: "auto" }}>
              <Box
                onMouseEnter={() => setHoveredIndex({ data: item, index })}
                onMouseLeave={() => setHoveredIndex(null)}
                sx={{ height: "100%" }}
              >
                {cardContent(item, index)}
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>

        <Button
          target="_blank"
          href="/special-offers"
          variant="outlined"
          color="primary"
          size="large"
          sx={{
            textTransform: "capitalize",
            mx: "auto",
            mb: 5,
            mt: 1,
          }}
        >
          {t("seeAllAccommodations")}
        </Button>
      </Box>
      {selectedAccommodation && (
        <BookModal
          accommodation={selectedAccommodation}
          open={bookModalOpen}
          onClose={() => {
            setBookModalOpen(false);
            setSelectedAccommodation(null);
          }}
        />
      )}
    </>
  );
}

// This is just for demo purposes - in a real app, you'd fetch this from the API
const demoData = {
  // The API response data would be used here
};
